import React from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  TreeNodeData,
  buildPathLabel,
  buildTree,
  collectFolderIds,
  extractExtension,
} from 'pages/workspace/components/treeUtils';
import { WorkSpaceNode } from 'stores/WorkSpaceStoreAdapter';

type Props = {
  open: boolean;
  onClose: () => void;
  /** ワークスペースのフラットノード一覧 */
  nodes: WorkSpaceNode[];
  loading: boolean;
  /** 選択可能なファイル拡張子（小文字。例: ['md','knz']） */
  selectableExtensions: string[];
  /** ファイル確定時（fileId / name / extension / path を返す。path はツリーから組み立てた絶対パス） */
  onSelect: (file: { fileId: string; name: string; extension: string; path: string }) => void;
};

/**
 * Windows / Mac のファイル選択エクスプローラー風のファイル選択モーダル。
 * ワークスペースのフォルダ/ファイル階層をツリー表示し、選択可能な拡張子のファイルのみ選べる。
 * 記事の新規公開でファイル（本文）を選ぶ用途。
 */
const FilePickerDialog: React.FC<Props> = ({
  open,
  onClose,
  nodes,
  loading,
  selectableExtensions,
  onSelect,
}) => {
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const tree = React.useMemo(() => buildTree(nodes), [nodes]);

  // 開くたびに全フォルダ展開・選択リセット
  React.useEffect(() => {
    if (open) {
      setExpandedIds(new Set(collectFolderIds(nodes)));
      setSelectedId(null);
    }
  }, [open, nodes]);

  const isSelectableFile = (node: WorkSpaceNode): boolean => {
    if (node.type !== 'FILE' || !node.fileId) return false;
    const ext = extractExtension(node.name);
    return ext !== null && selectableExtensions.includes(ext);
  };

  const toggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedNode = nodes.find((n) => n.id === selectedId) ?? null;
  const canConfirm = selectedNode !== null && isSelectableFile(selectedNode);

  /** ノードの絶対パス（例: /docs/capital/article.knz）をツリーから組み立てる */
  const buildNodePath = (node: WorkSpaceNode): string => {
    const dir = buildPathLabel(node.parentId, nodes); // ルート直下は "/"
    return dir === '/' ? `/${node.name}` : `${dir}/${node.name}`;
  };

  const handleConfirm = () => {
    if (!selectedNode || !selectedNode.fileId) return;
    onSelect({
      fileId: selectedNode.fileId,
      name: selectedNode.name,
      extension: extractExtension(selectedNode.name) ?? '',
      path: buildNodePath(selectedNode),
    });
    onClose();
  };

  const renderNode = (node: TreeNodeData, depth: number): React.ReactNode => {
    const isFolder = node.type === 'FOLDER';
    const isExpanded = expandedIds.has(node.id);
    const isSelected = selectedId === node.id;
    const selectable = isSelectableFile(node);
    const disabled = !isFolder && !selectable;

    return (
      <React.Fragment key={node.id}>
        <Box
          onClick={() => {
            if (isFolder) {
              setSelectedId(node.id);
              toggle(node.id);
            } else if (selectable) {
              setSelectedId(node.id);
            }
          }}
          onDoubleClick={() => {
            if (selectable) {
              setSelectedId(node.id);
              // ダブルクリックで即確定
              onSelect({
                fileId: node.fileId as string,
                name: node.name,
                extension: extractExtension(node.name) ?? '',
                path: buildNodePath(node),
              });
              onClose();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pl: depth * 1.5 + 0.5,
            pr: 1,
            py: 0.5,
            borderRadius: 1,
            cursor: disabled ? 'default' : 'pointer',
            userSelect: 'none',
            opacity: disabled ? 0.45 : 1,
            bgcolor: isSelected ? 'action.selected' : 'transparent',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: disabled ? 'transparent' : 'action.hover' },
          }}
        >
          {isFolder ? (
            isExpanded ? (
              <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            )
          ) : (
            <Box sx={{ width: 18 }} />
          )}
          {isFolder ? (
            isExpanded ? (
              <FolderOpenIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            ) : (
              <FolderIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            )
          ) : (
            <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
          )}
          <Typography variant="body2" noWrap>
            {node.name}
          </Typography>
        </Box>
        {isFolder && isExpanded && node.children.map((c) => renderNode(c, depth + 1))}
      </React.Fragment>
    );
  };

  const extLabel = selectableExtensions.map((e) => `.${e}`).join(' / ');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: { elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          本文ファイルを選択
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          記事にできるのは {extLabel} ファイルのみです。ダブルクリックで確定できます。
        </Typography>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            height: 320,
            overflow: 'auto',
            py: 0.5,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : tree.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 1.5, py: 1, display: 'block' }}
            >
              ファイルがありません
            </Typography>
          ) : (
            tree.map((n) => renderNode(n, 0))
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Stack direction="row" sx={{ flex: 1, minWidth: 0 }} alignItems="center">
          {selectedNode && isSelectableFile(selectedNode) && (
            <Typography variant="caption" color="text.secondary" noWrap>
              選択中: {selectedNode.name}
            </Typography>
          )}
        </Stack>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          選択
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePickerDialog;
